import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  attachReceipt,
  fetchMatchTargets,
  getReconciliationQueue,
  proposeMatch,
  type AttachReceiptArgs,
  type FetchMatchTargetsArgs,
  type ProposeMatchArgs,
} from '../api/reconciliation';

const QUEUE_KEY = ['reconciliation', 'queue'] as const;

/**
 * Live reconciliation queue. Used by the chat-embedded card to render
 * up-to-date counts + resolved row state even when the chat artifact
 * is a stale snapshot from an earlier turn.
 *
 * Pass `monthlyCloseId` to scope counts to the same monthly close the
 * artifact was emitted for — otherwise cross-month data leaks into
 * a per-statement card (e.g. May card shows "Needs help: 98" when only
 * 14 May transactions exist in scope).
 */
export function useReconciliationQueue(
  monthlyCloseId?: string,
  enabled: boolean = true,
) {
  return useQuery({
    queryKey: [...QUEUE_KEY, monthlyCloseId ?? '__all'] as const,
    queryFn: () => getReconciliationQueue({ monthlyCloseId }),
    enabled,
    staleTime: 30_000,
  });
}

export function useAttachReceipt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: AttachReceiptArgs) => attachReceipt(args),
    onSuccess: () => {
      // Refresh both the recon queue (counts shift) and the document list
      // (paymentStatus changes once attached).
      qc.invalidateQueries({ queryKey: QUEUE_KEY });
      qc.invalidateQueries({ queryKey: ['documents'] });
    },
  });
}

export function useMatchTargets(args: FetchMatchTargetsArgs, enabled: boolean = true) {
  return useQuery({
    queryKey: ['reconciliation', 'match-targets', args] as const,
    queryFn: () => fetchMatchTargets(args),
    enabled,
    staleTime: 15_000,
  });
}

export function useProposeMatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: ProposeMatchArgs) => proposeMatch(args),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QUEUE_KEY });
      qc.invalidateQueries({ queryKey: ['reconciliation', 'match-targets'] });
    },
  });
}
